import { useDeviceType } from '@cian/ui-kit/responsive';
import cx from 'classnames';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as styles from './Tags.css';
import { modifyJsonQuery, TModifier } from '../../../../../packages/Filters';
import { getTermsValue, NonEmptyArray } from '../../../../../packages/JsonQuery';
import { IJsonQuery } from '../../../../../packages/api-models/common/json_query';
import { fetchHybridSearchData } from '../../../../common/actions/fetchHybridSearchData';
import { changeJsonQuery, fetchTags, IJsonQueryChanged, removeTag } from '../../../../common/actions/filters';
import { Tag } from '../../../../common/components/Tag';
import { TagsCollapsible } from '../../../../common/components/TagsCollapsible';
import { DESKTOP_SIDEBAR_WIDTH } from '../../../../common/constants/desktopSidebar';
import { PIK_PROMO } from '../../../../common/constants/pik';
import { selectIsDesktopListingVisible } from '../../../../common/selectors/desktopListing';
import { ITagsState } from '../../../../common/types/filters';
import { IApplicationState, TThunkDispatch } from '../../../../common/types/redux';
import { TTag } from '../../../../common/types/tags';

export const Tags: React.FC = () => {
  const deviceType = useDeviceType();
  const dispatch = useDispatch<TThunkDispatch>();
  const jsonQuery = useSelector<IApplicationState, IJsonQuery>(state => state.filters.jsonQuery);
  const tags = useSelector<IApplicationState, ITagsState>(state => state.filters.tags);
  const isDesktopListingVisible = useSelector(selectIsDesktopListingVisible);

  const handleRemove = React.useCallback(
    (tag: TTag) => {
      switch (tag.type) {
        case 'address':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, { action: 'removeGeo', arguments: [{ type: tag.geoType, id: tag.id }] }),
            ),
          );
          break;
        case 'developer': {
          const modifiers: [TModifier, ...TModifier[]] = [
            { action: 'removeGeo', arguments: [{ type: 'builder', id: tag.id }] },
          ];

          if (tag.id === PIK_PROMO.developerId) {
            modifiers.push({ action: 'setPikPromo', arguments: [null] });
          }

          dispatch(changeJsonQuery(applyModifiers(jsonQuery, ...modifiers)));
          break;
        }
        case 'distance':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, {
                action: 'removeGeo',
                arguments: [{ type: 'distance', center: tag.center, radius: tag.radius, name: tag.name }],
              }),
            ),
          );
          break;
        case 'district':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, {
                action: 'removeGeo',
                arguments: [{ type: 'district', id: tag.id }],
              }),
            ),
          );
          break;
        case 'polygon':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, {
                action: 'removeGeo',
                arguments: [{ type: 'polygon', coordinates: tag.coordinates, name: tag.name }],
              }),
            ),
          );
          break;
        case 'railway':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, {
                action: 'removeGeo',
                arguments: [{ type: 'railway', id: tag.id }],
              }),
            ),
          );
          break;
        case 'region': {
          const currentRegions = getTermsValue('region')(jsonQuery);

          let nextRegions = currentRegions && currentRegions.filter(region => region !== tag.id);
          if (nextRegions && nextRegions.length === 0) {
            nextRegions = null;
          }

          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, {
                action: 'setRegion',
                arguments: [nextRegions as NonEmptyArray<number>],
              }),
            ),
          );
          break;
        }

        case 'residentialComplex':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, {
                action: 'removeGeo',
                arguments: [{ type: 'newobject', id: tag.id }],
              }),
            ),
          );
          break;
        case 'underground':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, {
                action: 'removeGeo',
                arguments: [{ type: 'underground', id: tag.id }],
              }),
            ),
          );
          break;
      }

      dispatch(removeTag(tag));
      dispatch(fetchTags());
      dispatch(fetchHybridSearchData({ deviceType }));
    },
    [dispatch, jsonQuery, deviceType],
  );

  if (deviceType !== 'desktop' || !tags.items) {
    return null;
  }

  return (
    <div
      className={styles['container']}
      style={{ '--sidebar-width': `${DESKTOP_SIDEBAR_WIDTH}px` } as React.CSSProperties}
    >
      <div className={styles['wrapper']}>
        <div className={cx(styles['content'], { [styles['content--ectopic']]: isDesktopListingVisible })}>
          <TagsCollapsible>
            {tags.items.map(tag => (
              <Tag key={`tag_${tag.type}_${tag.name}`} onRemove={() => handleRemove(tag)}>
                {tag.name}
              </Tag>
            ))}
          </TagsCollapsible>
        </div>
      </div>
    </div>
  );
};

function applyModifiers(jsonQuery: IJsonQuery, ...modifiers: [TModifier, ...TModifier[]]): IJsonQueryChanged {
  return {
    appliedModifiers: modifiers,
    prevJsonQuery: jsonQuery,
    nextJsonQuery: modifyJsonQuery(jsonQuery, modifiers),
  };
}
